import type { Locale } from "i18n/constants";
import { DEFAULT_LOCALE, LOCALES } from "i18n/constants";

export function generateCanonicalUrl({
  locale,
  slug
}: {
  locale?: Locale;
  slug: `/${string}`;
}): string {
  if (!locale || locale === DEFAULT_LOCALE) {
    return `https://www.gemini.com${slug}`;
  }

  return `https://www.gemini.com/${locale}${slug}`;
}

// Map locales to their hreflang codes (following Google's guidelines)
export const LOCALE_TO_HREFLANG = {
  "en-US": "en-US",
  "en-SG": "en-SG",
  "en-AU": "en-AU",
  "en-GB": "en-GB",
  es: "es-419",
  pt: "pt-BR",
  it: "it-IT",
  fr: "fr-FR"
} as const satisfies Record<Locale, string>;

// Map locales to valid HTML lang attribute values
const LOCALE_TO_HTML_LANG = {
  "en-US": "en-US",
  "en-SG": "en-SG",
  "en-AU": "en-AU",
  "en-GB": "en-GB",
  es: "es",
  pt: "pt-BR",
  it: "it-IT",
  fr: "fr-FR"
} as const satisfies Record<Locale, string>;

export function getHtmlLang(locale: Locale | string): string {
  if (locale in LOCALE_TO_HTML_LANG) {
    return LOCALE_TO_HTML_LANG[locale as Locale];
  }

  return "en";
}

export function generateAlternateLinks({
  slug,
  excludeLocales = []
}: {
  slug: `/${string}`;
  excludeLocales?: Locale[];
}): Record<string, string> {
  const alternates: Record<string, string> = {};

  for (const locale of LOCALES) {
    if (excludeLocales.includes(locale)) {
      continue;
    }

    const hreflang = LOCALE_TO_HREFLANG[locale];
    alternates[hreflang] = generateCanonicalUrl({ locale, slug });
  }

  // Add x-default (usually points to default locale)
  alternates["x-default"] = generateCanonicalUrl({
    locale: DEFAULT_LOCALE,
    slug
  });

  return alternates;
}
